/**
 * PID controller — cascaded architecture for the 2D rocket lander.
 *
 * Layer 1 — Guidance:
 *   Horizontal position error and horizontal velocity → desired pitch.
 *   Desired pitch is clamped to safe attitude limits.
 *
 * Layer 2 — Attitude:
 *   Desired pitch vs current pitch, with pitch rate damping → gimbal command.
 *   Output is NEGATED because tau_y = -d * T * sin(gimbal):
 *     positive gimbal produces negative pitch torque.
 *
 * Layer 3 — Vertical control:
 *   Vertical velocity error vs altitude-dependent target → throttle.
 *   PI control with gravity-compensation feedforward.
 *   Accounts for vertical component of thrust vector (division by cos(theta+gimbal)).
 *   Throttle clamped to [0, 1]; zero thrust when fuel exhausted.
 *
 * Anti-windup: integral terms are clamped.
 */

import { BaseController } from "./base";
import type { Environment, RocketParams, RocketState, ControlAction } from "../types";

export interface PIDConfig {
  /** Guidance: horizontal position error gain (1/m). */
  readonly kpX: number;
  /** Guidance: horizontal velocity damping gain (s/m). */
  readonly kdX: number;
  /** Maximum desired pitch angle (rad). */
  readonly maxPitch: number;
  /** Attitude: proportional gain (1/rad). */
  readonly kpTheta: number;
  /** Attitude: derivative gain on pitch rate (s/rad). */
  readonly kdTheta: number;
  /** Attitude: integral gain on pitch error (1/(rad·s)). */
  readonly kiTheta: number;
  /** Vertical: proportional gain on vertical velocity error (s/m). */
  readonly kpZ: number;
  /** Vertical: integral gain on vertical velocity error (1/m). */
  readonly kiZ: number;
  /** Maximum gimbal command (rad). */
  readonly maxGimbal: number;
}

export const DefaultPIDConfig: PIDConfig = {
  kpX: 0.004,
  kdX: 0.1,
  maxPitch: 0.3,
  kpTheta: 2.0,
  kdTheta: 0.4,
  kiTheta: 0.05,
  kpZ: 0.1,
  kiZ: 0.02,
  maxGimbal: 0.2,
};

export class PIDController extends BaseController {
  readonly name = "pid";

  private config: PIDConfig;
  private rocket: RocketParams;
  private integralTheta = 0;
  private integralVz = 0;

  constructor(rocket: RocketParams, config: Partial<PIDConfig> = {}) {
    super();
    this.rocket = rocket;
    this.config = { ...DefaultPIDConfig, ...config };
  }

  reset(): void {
    this.integralTheta = 0;
    this.integralVz = 0;
  }

  compute(state: RocketState, env: Environment, dt: number): ControlAction {
    const {
      kpX,
      kdX,
      maxPitch,
      kpTheta,
      kdTheta,
      kiTheta,
      kpZ,
      kiZ,
      maxGimbal,
    } = this.config;
    const { dryMass, maxThrust } = this.rocket;

    // --- Layer 1: Guidance — horizontal position/velocity → desired pitch ---
    const xError = -state.x; // target at x=0
    let thetaCmd = kpX * xError - kdX * state.vx;
    thetaCmd = Math.min(maxPitch, Math.max(-maxPitch, thetaCmd));

    // --- Layer 2: Attitude — desired pitch vs current → gimbal ---
    const eTheta = thetaCmd - state.theta;
    // Normalize angle error to [-pi, pi].
    let eThetaNorm = ((eTheta % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    if (eThetaNorm > Math.PI) {
      eThetaNorm -= 2 * Math.PI;
    }

    // Integral of attitude error, clamped for anti-windup.
    this.integralTheta += eThetaNorm * dt;
    const integralMax = 2;
    this.integralTheta = Math.min(integralMax, Math.max(-integralMax, this.integralTheta));

    // Attitude PID output, then NEGATE because tau_y = -d * T * sin(gimbal).
    const attitudeOutput =
      kpTheta * eThetaNorm + kdTheta * (-state.omega) + kiTheta * this.integralTheta;
    let gimbalCmd = -attitudeOutput;
    gimbalCmd = Math.min(maxGimbal, Math.max(-maxGimbal, gimbalCmd));

    // --- Layer 3: Vertical — vertical velocity error → throttle ---
    // Altitude-dependent descent velocity profile.
    const flareAltitude = 80;
    const descentRate = 12;
    let vzTarget: number;
    if (state.z > flareAltitude) {
      vzTarget = -descentRate;
    } else if (state.z > 0) {
      vzTarget = -descentRate * (state.z / flareAltitude);
    } else {
      vzTarget = 0;
    }

    const eVz = vzTarget - state.vz;

    // PI control on vertical velocity error.
    this.integralVz += eVz * dt;
    const vzIntegralMax = 1000;
    this.integralVz = Math.min(vzIntegralMax, Math.max(-vzIntegralMax, this.integralVz));

    const aDes = kpZ * eVz + kiZ * this.integralVz;

    // Required vertical force: gravity compensation feedforward + acceleration.
    const mass = dryMass + state.fuel;
    const fzReq = mass * (env.gravity + aDes);

    // Account for vertical component of thrust vector.
    const thetaPlusGimbal = state.theta + gimbalCmd;
    const cosTerm = Math.cos(thetaPlusGimbal);
    const safeCos = Math.abs(cosTerm) < 0.1 ? (cosTerm < 0 ? -0.1 : 0.1) : cosTerm;

    let throttleCmd = fzReq / (maxThrust * safeCos);
    throttleCmd = Math.min(1, Math.max(0, throttleCmd));

    // Fuel exhaustion: no thrust if out of fuel.
    if (state.fuel <= 0) {
      throttleCmd = 0;
    }

    return { throttle: throttleCmd, gimbal: gimbalCmd };
  }
}
